import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { useCallback, useEffect, useRef, useState } from "react";
import { setColumnWidth } from "../../../helpers/cookieUtils";
import ListingHeader from "../listingHeader/ListingHeader";
import {
  Tooltip,
  TableCellLayout,
  Avatar,
  Popover,
  PopoverTrigger,
  PopoverSurface,
  makeStyles,
} from "@fluentui/react-components";
import HoverCard from "../../hoverCard/HoverCard";
import useNavigation from "../../../utils/useNavigation";
import {
  AttachementIcon,
  DeleteIcon,
  EditIcon,
  FlagIcon,
  MeatBallsMenuIcon,
  PendingDeleteIcon,
  RemindIcon,
} from "../../../modules/default-svg";
import {
  getAccountCurrency,
  displayDecimalFormatted,
  dateOnlyFormatted,
  localDateFormatted,
  isDateGreaterThan
} from "../../../utils/systemSettings";
import userService from "../../../store/services/user.service";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import { generalActions } from "../../../store/actions";
import CustomSelect from "../select/CustomSelect";
import Spin from "../spinner/Spin";
import {
  createProjectRequest,
  updateProjectFlag,
  updateProjectRequest,
} from "../../../store/actions/projects.actions";
import clientService from "../../../store/services/clients.service";
import { apiCall } from "../../../store/services/service";
import {
  compareArrays,
  getDeletePermission,
  formatSequenceNumber,
  getEditPermission,
  getLabelByType,
  getProjectStatusPillClass,
  projectStatus,
  renderHealthStatus,
  sortColumnsByOrderlist,
} from "../../../helpers/utils";
import {
  BASE_URL,
  URL_SEARCH_CLIENT_PORTFOLIO,
} from "../../../store/services/URL";
import {
  isValidDate,
  localDateToISODateString,
} from "../../../utils/systemSettings";
import { createSkeletonRows, useTableContextChange } from "./utils/tableUtils";

const EditInput = ({
  label,
  value,
  onChange,
  type,
  onBlur,
  dateFormat,
  clientId,
  portfolioId,
  parentType,
  table,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [resources, setResources] = useState([]);
  const [clients, setClients] = useState([]);
  const [portfolioOptions, setPortfolioOptions] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [dropdownOptions, setDropdownOptions] = useState([]);

  const formatValue = (val) => {
    if (!val) return null;
    if (val.label && val.value) return val;
    return { label: val.name, value: val.id };
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const creatingRowState = table.getState().creatingRow;
      if (table?.options?.onCreatingRowSave && creatingRowState) {
        const currentValues = {
          ...creatingRowState.values,
        };
        table.options.onCreatingRowSave({
          values: currentValues,
          table,
        });
      }
    }
  };

  const handleDateChange = (newDate) => {
    if (isValidDate(newDate)) {
      const newValue = localDateToISODateString(newDate);
      setInputValue(newValue);
      onChange(newValue);
    }
  };

  const searchHandler = (value) => {
    if (value === "") {
      setResources([]);
      return;
    }
    userService
      .searchUserDataRequest(value)
      .then((res) => {
        const transformedArray = res.map((obj) => ({
          label: obj.name,
          value: obj.id,
        }));
        setResources(transformedArray);
      })
      .catch(() => {
        setResources([]);
      });
  };

  const searchClientHandler = (value) => {
    if (value === "") {
      setClients([]);
      return;
    }
    clientService
      .searchClientsRequest(value)
      .then((res) => {
        const transformedArray = res.map((obj) => ({
          label: obj.name,
          value: obj.id,
        }));
        setClients(transformedArray);
      })
      .catch(() => {
        setClients([]);
      });
  };

  const searchPortfolioHandler = (value) => {
    if (value === "") {
      setPortfolioOptions([]);
      return;
    }
    let queryParam = `?clientId=${clientId}&searchTerm=${value}`;
    apiCall
      .get({
        url: `${URL_SEARCH_CLIENT_PORTFOLIO}${queryParam}`,
        isAuthToken: true,
      })
      .then((res) => {
        const transformedArray = res.map((obj) => ({
          label: obj.name,
          value: obj.id,
        }));
        setPortfolioOptions(transformedArray);
      })
      .catch(() => {
        setPortfolioOptions([]);
      });
  };

  const searchProgramHandler = (value) => {
    if (value === "") {
      setPrograms([]);
      return;
    }
    const payload = { searchText: value };
    let queryParam = `?searchTerm=${payload.searchText}`;

    payload.parent = "Project";
    if (parentType === "Portfolio") {
      queryParam = queryParam + `&portfolioId=${portfolioId}`;
    }

    apiCall
      .get({ url: `${BASE_URL}search/Programs${queryParam}` })
      .then((res) => {
        const transformedArray = res.map((obj) => ({
          label: obj.name,
          value: obj.id,
        }));
        setPrograms(transformedArray);
      })
      .catch(() => {
        setPrograms([]);
      });
  };

  const dropdownHandler = () => {
    if (label === "Status") {
      setDropdownOptions(() =>
        projectStatus.map((item) => ({
          label: item.name,
          value: item.value,
        }))
      );
    }
  };

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const handleInputChange = (newValue) => {
    setInputValue(newValue);
    if (onChange) {
      onChange(newValue);
    }
  };

  const handleSelectChange = (item) => {
    if (!item) return;
    const newValue = { id: item.value, name: item.label };
    handleInputChange(newValue);
  };

  if (type === "text") {
    return (
      <input
        placeholder={`Enter ${label}`}
        value={inputValue}
        type="text"
        onChange={(e) => handleInputChange(e.target.value)}
        className="Custom-Input"
        onBlur={onBlur}
        onKeyDown={handleKeyDown}
      />
    );
  }

  if (type === "number") {
    return (
      <input
        placeholder={`Enter ${label}`}
        value={inputValue}
        type="number"
        min={0}
        onChange={(e) => handleInputChange(+e.target.value)}
        className="Custom-Input"
        onBlur={onBlur}
        onKeyDown={handleKeyDown}
      />
    );
  }

  if (type === "select") {
    let searchTextFunc = searchHandler;
    let options = resources;
    let selectText = "manager";
    if (label === "Client") {
      searchTextFunc = searchClientHandler;
      options = clients;
      selectText = "client";
    } else if (label === "Portfolio") {
      searchTextFunc = searchPortfolioHandler;
      options = portfolioOptions;
      selectText = "portfolio";
    } else if (label === "Program") {
      searchTextFunc = searchProgramHandler;
      options = programs;
      selectText = "program";
    } else if (label === "Status") {
      searchTextFunc = dropdownHandler;
      options = dropdownOptions;
      selectText = "status";
    }

    return (
      <div className="d-flex align-items-center w-full max-w-md mx-auto">
        <CustomSelect
          searchText={searchTextFunc}
          SelectText={selectText}
          CustomClass="ModalAvatarSearch CustomSelect"
          options={options}
          value={formatValue(value)}
          onChange={handleSelectChange}
          creatable={true}
          setCreated={handleSelectChange}
          handleBlur={onBlur}
          isDatatable
          selectWd="w-100"
          onKeyDown={handleKeyDown}
        />
      </div>
    );
  }

  return (
    <DatePicker
      selected={inputValue ? moment(inputValue).toDate() : null}
      onChange={handleDateChange}
      dateFormat={dateFormat}
      className="CustomDatePickerInput"
      onBlur={onBlur}
      popperPlacement="bottom-start"
      placeholderText={dateFormat}
      onKeyDown={handleKeyDown}
      popperClassName="CustomDatePickerPopper"
      showPopperArrow={false}
    />
  );
};

const useStyles = makeStyles({
  tooltip: {
    backgroundColor: "#342c82",
    color: "#FFFFFF",
  },
});

const ProjectTable = (props) => {
  const {
    columnsList,
    rowData,
    setIsCreate,
    searchHandler,
    tableName,
    handleColumnOrdering,
    dispatch,
    setPageNum,
    perPageItems,
    pagination,
    handleReminder,
    handleEdit,
    handleDelete,
    parentType,
    clientId,
    portfolioId,
    programId,
    projectId,
    projectLoading,
    handleAddRow,
    handleCancelAddRow,
    setRowData,
    isHeader,
    triggerSearch
  } = props;

  const styles = useStyles();
  const [cols, setCols] = useState([]);
  const [rows, setRows] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const setting = JSON.parse(localStorage.getItem("settings"));
  const [editedRows, setEditedRows] = useState({});
  const tableContainerRef = useRef(null);
  const lastScrollTop = useRef(0);
  const selectRefs = useRef({});
  const { navigateTo } = useNavigation();
  const [editingHeader, setEditingHeader] = useState(null);
  const [headerValue, setHeaderValue] = useState("");
  const [columnSizing, setColumnSizing] = useState({});
  const [columnOrder, setColumnOrder] = useState([]);
  const [sorting, setSorting] = useState([]);
  const [popoverStates, setPopoverStates] = useState({});
  const [colsList, setColumnsList] = useState([]);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const { hasContextChanged } = useTableContextChange({
    contextValues: {
      clientId,
      portfolioId,
      programId,
      projectId,
      parentType
    },
    setIsFetchingMore,
    setRows,
    setRowData,
  }
  );

  const handlePopoverChange = (itemId, isOpen) => {
    setPopoverStates(isOpen ? { [itemId]: isOpen } : {});
  };

  useEffect(() => {
    if (columnsList.length) {
      setColumnsList(columnsList);
    }
  }, [columnsList]);
  const handleHeaderDoubleClick = (columnId, currentHeader) => {
    setEditingHeader(columnId);
    setHeaderValue(currentHeader);
  };

  const handleHeaderSave = (columnId) => {
    if (!headerValue.trim()) {
      dispatch(
        generalActions.showToast({
          title: "Header name cannot be empty",
          type: "error",
        })
      );
      return;
    }

    const updatedColumnsDB = cols.map((col) => ({
      name: col.Label,
      customName: col.accessorKey === columnId ? headerValue : col.CustomName,
      order: col.Order,
      isVisible: col.Visibility,
    }));

    const payload = {
      Name: tableName,
      Columns: updatedColumnsDB,
    };

    userService
      .putUserLayoutRequest(payload)
      .then(() => { })
      .catch(() => {
        dispatch(
          generalActions.showToast({
            title: "Error updating column header",
            type: "error",
          })
        );
      })
      .finally(() => {
        setColumnsList((prevCols) => {
          const newCols = prevCols.map((col) =>
            col.accessorKey === columnId
              ? { ...col, header: headerValue, customName: headerValue }
              : col
          );
          return newCols;
        });
      });

    setEditingHeader(null);
    setHeaderValue("");
  };

  const handleRedirect = (item) => {
    // Use navigation hook instead of localStorage
    navigateTo("Projects", item.id, item.name);
  };

  const handleRedirectClient = (item) => {
    if (parentType !== "Client") {
      // Use navigation hook for direct navigation to client
      navigateTo("Clients", item.client.id, item.client.name);
    } else {
      // Already in client context, just navigate
      navigateTo("Clients", item.client.id, item.client.name);
    }
  };

  const handlePortfolioRedirect = (item) => {
    if (parentType !== "Portfolio") {
      // Use navigation hook for direct navigation to portfolio
      navigateTo("Portfolios", item.portfolio.id, item.portfolio.name);
    } else {
      // Already in portfolio context, just navigate
      navigateTo("Portfolios", item.portfolio.id, item.portfolio.name);
    }
  };

  const handleRedirectProgram = (item) => {
    if (item.program) {
      if (parentType !== "Program") {
        // Use navigation hook for direct navigation to program
        navigateTo("Programs", item.program.id, item.program.name);
      } else {
        // Already in program context, just navigate
        navigateTo("Programs", item.program.id, item.program.name);
      }
    }
  };

  const handleFlag = (id, status) => {
    const newData = [...rowData];
    const index = newData.findIndex((item) => item.id === id);
    if (index !== -1) {
      newData[index].isFlagged = status;
    }

    setRowData(
      newData.sort((a, b) => {
        return b.isFlagged - a.isFlagged;
      })
    );

    dispatch(
      updateProjectFlag({
        id: id,
        payload: [
          {
            op: "replace",
            path: "/IsFlagged",
            value: status,
          },
        ],
      })
    );
  };

  const fetchMoreData = useCallback(() => {
    if (!pagination?.HasNext || projectLoading || isFetchingMore || hasContextChanged) return;
    setIsFetchingMore(true);
    setPageNum(pagination.CurrentPage + 1);
  }, [pagination, projectLoading, isFetchingMore, setPageNum, hasContextChanged]);

  useEffect(() => {
    if (!projectLoading && isFetchingMore) {
      setIsFetchingMore(false);
    }
  }, [projectLoading, rowData.length]);

  const fetchMoreOnBottomReached = useCallback(
    (containerRefElement) => {
      if (!containerRefElement || projectLoading || !pagination?.HasNext) return;

      const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
      const scrolledToBottom = Math.abs(scrollHeight - scrollTop - clientHeight) < 50;
      const isScrollingDown = scrollTop > lastScrollTop.current;

      lastScrollTop.current = scrollTop;

      if (isScrollingDown && scrolledToBottom) {
        fetchMoreData();
      }
    },
    [fetchMoreData, projectLoading, pagination]
  );

  const throttledFetchMore = useCallback(
    (() => {
      let timeoutId;
      return (containerRefElement) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          fetchMoreOnBottomReached(containerRefElement);
        }, 100);
      };
    })(),
    [fetchMoreOnBottomReached]
  );

  useEffect(() => {
    const container = tableContainerRef.current;
    if (container) {
      const handleScroll = () => throttledFetchMore(container);
      container.addEventListener("scroll", handleScroll, { passive: true });
      return () => {
        container.removeEventListener("scroll", handleScroll);
      };
    }
  }, [throttledFetchMore]);

  // Reset scroll position when search is triggered
  useEffect(() => {
    if (tableContainerRef.current && triggerSearch !== undefined) {
      tableContainerRef.current.scrollTop = 0;
      lastScrollTop.current = 0;
    }
  }, [triggerSearch]);

  const formatPayload = (payload) => {
    const formattedPayload = {};
    Object.keys(payload).forEach((key) => {
      formattedPayload[key] = payload[key];
    });
    return formattedPayload;
  };

  const handleSave = (rowId, payload) => {
    let data = { id: rowId, payload };
    data.parent = parentType;
    data.perPageItems = perPageItems;
    if (clientId) {
      data.parentId = clientId;
      data.parent = "Client";
    }
    if (portfolioId) {
      data.parentId = portfolioId;
      data.parent = "Portfolio";
    }

    if (programId) {
      data.parentId = programId;
      data.parent = "Program";
    }

    dispatch(updateProjectRequest(data));
    setEditedRows({});
  };

  const handleCreate = (payload) => {
    const data = { payload };
    data.perPageItems = perPageItems;
    if (clientId) {
      data.parentId = clientId;
      data.parent = "Client";
    }
    if (portfolioId) {
      data.parentId = portfolioId;
      data.parent = "Portfolio";
    }
    if (programId) {
      data.parentId = programId;
      data.parent = "Program";
    }
    dispatch(createProjectRequest(data));
  };

  const getSortingFunction = (col) => {
    switch (col.label) {
      case "Project":
        return (rowA, rowB, columnId) => {
          const a = (rowA.original.name || "").toLowerCase();
          const b = (rowB.original.name || "").toLowerCase();
          return a.localeCompare(b);
        };

      case "Sequence #":
        return (rowA, rowB, columnId) => {
          const a = rowA.original.sequenceNumber || 0;
          const b = rowB.original.sequenceNumber || 0;
          return a - b;
        };

      case "Client":
        return (rowA, rowB, columnId) => {
          const a = (rowA.original.client?.name || "").toLowerCase();
          const b = (rowB.original.client?.name || "").toLowerCase();
          return a.localeCompare(b);
        };

      case "Portfolio":
        return (rowA, rowB, columnId) => {
          const a = (rowA.original.portfolio?.name || "").toLowerCase();
          const b = (rowB.original.portfolio?.name || "").toLowerCase();
          return a.localeCompare(b);
        };

      case "Program":
        return (rowA, rowB, columnId) => {
          const a = (rowA.original.program?.name || "").toLowerCase();
          const b = (rowB.original.program?.name || "").toLowerCase();
          return a.localeCompare(b);
        };

      case "Project Manager":
        return (rowA, rowB, columnId) => {
          const a = (rowA.original.owner?.name || "").toLowerCase();
          const b = (rowB.original.owner?.name || "").toLowerCase();
          return a.localeCompare(b);
        };

      case "Status":
        return (rowA, rowB, columnId) => {
          const a = rowA.original.status || 0;
          const b = rowB.original.status || 0;
          return a - b;
        };

      case "Type":
        return (rowA, rowB, columnId) => {
          const a = rowA.original.type || 0;
          const b = rowB.original.type || 0;
          return a - b;
        };

      case "Budget":
        return (rowA, rowB, columnId) => {
          const a = parseFloat(rowA.original.budget) || 0;
          const b = parseFloat(rowB.original.budget) || 0;
          return a - b;
        };

      case "Actual":
        return (rowA, rowB, columnId) => {
          const a = parseFloat(rowA.original.actual) || 0;
          const b = parseFloat(rowB.original.actual) || 0;
          return a - b;
        };

      case "Accural":
        return (rowA, rowB, columnId) => {
          const a = parseFloat(rowA.original.accural) || 0;
          const b = parseFloat(rowB.original.accural) || 0;
          return a - b;
        };

      // case "ETC":
      //   return (rowA, rowB, columnId) => {
      //     const a = parseFloat(rowA.original.etc) || 0;
      //     const b = parseFloat(rowB.original.etc) || 0;
      //     return a - b;
      //   };

      // case "BAC":
      //   return (rowA, rowB, columnId) => {
      //     const a = parseFloat(rowA.original.bac) || 0;
      //     const b = parseFloat(rowB.original.bac) || 0;
      //     return a - b;
      //   };

      case "Start Date":
        return (rowA, rowB, columnId) => {
          const a = new Date(rowA.original.startDate || 0);
          const b = new Date(rowB.original.startDate || 0);
          return a.getTime() - b.getTime();
        };

      case "End Date":
        return (rowA, rowB, columnId) => {
          const a = new Date(rowA.original.endDate || 0);
          const b = new Date(rowB.original.endDate || 0);
          return a.getTime() - b.getTime();
        };

      case "Industry":
        return (rowA, rowB, columnId) => {
          const a = (rowA.original.industries || []).map((i) => i.name).join(", ").toLowerCase();
          const b = (rowB.original.industries || []).map((i) => i.name).join(", ").toLowerCase();
          return a.localeCompare(b);
        };

      default:
        return "alphanumeric";
    }
  };

  const getSortDescFirst = (col) => {
    const descFirstColumns = [
      "Budget",
      "Actual",
      "Accural",
      // "ETC",
      // "BAC",
      "Start Date",
      "End Date",
    ];
    return descFirstColumns.includes(col.label);
  };

  useEffect(() => {
    if (!colsList?.length && !rowData.length) return;

    const baseRows = [...rowData];

    if (isFetchingMore && pagination?.HasNext) {
      const skeletonRows = createSkeletonRows();
      setRows([...baseRows, ...skeletonRows]);
    } else {
      setRows(baseRows);
    }
  }, [rowData, isFetchingMore, pagination?.HasNext, colsList]);

  useEffect(() => {
    if (!colsList?.length && !rowData.length) return;

    const dateFormat = setting?.regionalSettings?.dateFormat ? setting?.regionalSettings?.dateFormat.replace(/dd/g, 'DD').replace(/yy/g, 'YY').replace(/mm/g, 'MM') : 'YY-MM-DD';
    const endOfCurrentWeek = moment().isoWeekday(5).endOf('day').format(`${dateFormat}`);
    const endOfNextWeek = moment().add(1, 'weeks').isoWeekday(5).endOf('day').format(`${dateFormat}`);

    const columnsOrder = colsList
      .filter((col) => col.isVisible !== false)
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map((item) => item.accessorKey);
    setColumnOrder(columnsOrder);

    const columns = colsList
      .filter((col) => col.isVisible !== false)
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map((col, index) => {
        return {
          Label: col.label,
          CustomName: col.customName,
          Order: col.order,
          Visibility: col.isVisible,
          accessorKey: col.accessorKey,
          header: col.header,
          enableHiding: true,
          enableColumnActions: false,
          grow: true,
          enableResizing: true,
          size: col.size,
          editVariant: col.columnType,
          enableGlobalFilter: true,
          enableEditing:
            col.accessorKey === "actual" ||
              col.accessorKey === "accural" ||
              col.accessorKey === "etc" ||
              col.accessorKey === "bac"
              ? false
              : true,
          enableSorting: editingHeader === col.accessorKey ? false : true,
          sortingFn: getSortingFunction(col),
          sortDescFirst: getSortDescFirst(col),
          muiEditTextFieldProps: ({ cell, row }) => ({
            sx: {
              fontFamily: "Figtree-Regular",
              fontSize: "14px",
              padding: "0.5rem",
              maxHeight: "28px",
            },
            required: col.required || false,
            error: !!validationErrors?.[`${row.id}_${cell.column.id}`],
            helperText: validationErrors?.[`${row.id}_${cell.column.id}`],
            onFocus: () =>
              setValidationErrors({
                ...validationErrors,
                [cell.column.id]: undefined,
              }),
            inputRef: (el) => {
              if (col.columnType === "select") {
                selectRefs.current[`${row.id}_${cell.column.id}`] = el;
              }
            },
            onChange: (event) => {
              const newValue = event.target.value;
              setEditedRows((prev) => ({
                ...prev,
                [row.id]: {
                  ...prev[row.id],
                  [cell.column.id]: newValue,
                },
              }));
            },
            onBlur: (event) => {
              const value = event.target.value;
              const validationError =
                col.required && !value ? "Required" : undefined;
              setValidationErrors((prev) => ({
                ...prev,
                [`${row.id}_${cell.column.id}`]: validationError,
              }));
            },
          }),
          Cell: ({ cell, row }) => {
            const item = row.original;

            // Render skeleton for skeleton rows
            if (item.isSkeleton) {
              return (
                <div
                  style={{
                    height: '20px',
                    background: 'linear-gradient(90deg, #e0e0e0 25%, #ECE7FD 50%, #e0e0e0 75%)',
                    backgroundSize: '200% 100%',
                    animation: 'shimmer 1.5s infinite',
                    borderRadius: '4px',
                    width: '100%'
                  }}
                />
              );
            }

            switch (col.label) {
              case "Project":
                return (
                  <div className="d-flex justify-content-between align-items-center inputWidth">
                    <div className="d-flex align-items-center inputWidth">
                      <Tooltip
                        positioning="before"
                        content={{
                          children: item.isFlagged ? "Unflag" : "Flag",
                          className: styles.tooltip,
                        }}
                        relationship="label"
                        withArrow
                      >
                        <span
                          className={`flag ${item.isFlagged ? "flagActive" : ""
                            }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleFlag(item.id, !item.isFlagged);
                          }}
                        >
                          <FlagIcon />
                        </span>
                      </Tooltip>
                      <div className="flex justify-center gap-1 mr-2 min-w-[40px]">
                        {renderHealthStatus({ period: endOfCurrentWeek, status: item.health?.current })}
                        {renderHealthStatus({ period: endOfNextWeek, status: item.health?.trending, isForecast: true })}
                      </div>
                      <span
                        className="d-flex w-full text-truncate"
                        onClick={() => handleRedirect(item)}
                      >
                        {item.attachementsCount > 0 && (
                          <div className="mr-1">
                            <AttachementIcon count={item.attachementsCount} />
                          </div>
                        )}
                        {item.isPendingDelete && (
                          <div className="mr-1">
                            <PendingDeleteIcon />
                          </div>
                        )}
                        {item.name}
                      </span>
                      <div className="meatballs-trigger">
                        <Popover
                          open={popoverStates[item.id]}
                          onOpenChange={(e, data) => handlePopoverChange(item.id, data.open || false)}
                          positioning="below-start"
                        >
                          <PopoverTrigger disableButtonEnhancement>
                            <TableCellLayout
                              media={<MeatBallsMenuIcon />}
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                handlePopoverChange(
                                  item.id,
                                  !popoverStates[item.id]
                                );
                              }}
                            />
                          </PopoverTrigger>
                          <PopoverSurface
                            tabIndex={-1}
                            className="listingPopoverWrapper"
                          >
                            <div className="listingPopoverContent">
                              {getEditPermission("Project") && (
                                <div
                                  className="popItem"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    handleEdit(item);
                                    handlePopoverChange(
                                      item.id,
                                      !popoverStates[item.id]
                                    );
                                  }}
                                >
                                  <EditIcon />
                                  <p className="text-sm-400 ms-12">Edit</p>
                                </div>
                              )}
                              <div
                                className="popItem"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  handleReminder(item);
                                  handlePopoverChange(
                                    item.id,
                                    !popoverStates[item.id]
                                  );
                                }}
                              >
                                <RemindIcon />
                                <p className="text-sm-400 ms-12">Remind me</p>
                              </div>
                              {getDeletePermission("Project", item) && (
                                <div
                                  className="popItem"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    handlePopoverChange(
                                      item.id,
                                      !popoverStates[item.id]
                                    );
                                    handleDelete(item);
                                  }}
                                >
                                  <DeleteIcon />
                                  <p className="text-sm-400 ms-12">Delete</p>
                                </div>
                              )}
                            </div>
                          </PopoverSurface>
                        </Popover>
                      </div>
                    </div>
                  </div>
                );
              case "Sequence #":
                return formatSequenceNumber("Project", item.sequenceNumber);
              case "Client":
                return (
                  <span onClick={() => handleRedirectClient(item)}>
                    {item?.client?.name || ""}
                  </span>
                );
              case "Portfolio":
                return (
                  <span onClick={() => handlePortfolioRedirect(item)}>
                    {item.portfolio?.name}
                  </span>
                );
              case "Program":
                return (
                  <span onClick={() => handleRedirectProgram(item)}>
                    {item.program ? item.program.name : ""}
                  </span>
                );
              case "Project Manager":
                return item.owner?.name ? (
                  <TableCellLayout
                    media={
                      <HoverCard
                        HoverCardTrigger={
                          <Avatar
                            color="colorful"
                            name={item.owner ? item.owner.name : ""}
                            image={{
                              src: item.owner.image ? item.owner.image : "",
                            }}
                            size={20}
                          />
                        }
                        AvatarId={item.owner?.id}
                        AvatarName={item.owner?.name}
                        AvatarImage={item.owner?.image}
                        AvatarDesignation={item.owner?.title}
                        AvatarEmail={item.owner?.email}
                        AvatarPhone={item.owner?.phoneNumber}
                        AvatarIsExternal={item.owner?.isExternal}
                      />
                    }
                  >
                    {item.owner ? item.owner.name : ""}
                  </TableCellLayout>
                ) : (
                  ""
                );
              case "Status":
                return (
                  <div className="flex w-100 justify-center items-center">
                    <p
                      className={`w-100 ${getProjectStatusPillClass(item.status)}`}
                    >
                      {getLabelByType("project", item.status)}
                    </p>
                  </div>
                );
              case "Type":
                const backgroundColor = item.type == 0 ? "#EDECF8" : "#DBEAFE";
                const textColor = item.type == 0 ? "#342C82" : "#1E40AF";
                return (
                  <div className="flex w-100 justify-center items-center">
                    <p
                      style={{ backgroundColor: backgroundColor, color: textColor }}
                      className={`w-100 text-xs font-semibold text-center py-[4px] rounded-md`}
                    >
                      {`${getLabelByType("projectType", item.type)}`}
                    </p>
                  </div>
                );
              case "Budget":
                return `${getAccountCurrency(
                  setting
                )} ${displayDecimalFormatted(setting, item.budget)}`;
              case "Actual":
                return item.actual
                  ? `${getAccountCurrency(setting)} ${displayDecimalFormatted(
                    setting,
                    item.actual
                  )}`
                  : "";
              case "Accural":
                return item.accural
                  ? `${getAccountCurrency(setting)} ${displayDecimalFormatted(
                    setting,
                    item.accural
                  )}`
                  : "";
              // case "ETC":
              //   return item.etc
              //     ? `${getAccountCurrency(setting)} ${displayDecimalFormatted(
              //         setting,
              //         item.etc
              //       )}`
              //     : "";
              // case "BAC":
              //   return item.bac
              //     ? `${getAccountCurrency(setting)} ${displayDecimalFormatted(
              //         setting,
              //         item.bac
              //       )}`
              //     : "";
              case "Start Date":
                return item.startDate
                  ? dateOnlyFormatted(setting, item.startDate)
                  : "";
              case "End Date":
                return item.endDate
                  ? dateOnlyFormatted(setting, item.endDate)
                  : "";
              case "Industry":
                return (
                  item.industries?.length > 0 ? (
                    <div className="d-flex gap-1 text-nowrap overflow-hidden">
                      {item.industries.map((industry) => (
                        <span key={industry.id} className="pill secondary">
                          {industry.name}
                        </span>
                      ))}
                    </div>
                  ) : (
                    ""
                  )
                );
              default:
                return cell.getValue();
            }
          },
          Edit: ({ cell, row, table }) => {
            const value =
              editedRows[row.id]?.[cell.column.id] ??
              cell.getValue() ??
              row.original[cell.column.id] ??
              (col.columnType === "select" ? null : "");
            return (
              <EditInput
                label={col.label}
                value={value}
                onChange={(newValue) => {
                  if (row.id.startsWith("mrt-row-create")) {
                    table.setCreatingRow((prev) => ({
                      ...prev,
                      values: {
                        ...prev.values,
                        [cell.column.id]: newValue,
                      },
                    }));
                  } else {
                    setEditedRows((prev) => ({
                      ...prev,
                      [row.id]: {
                        ...prev[row.id],
                        [cell.column.id]: newValue,
                      },
                    }));
                  }
                }}
                onBlur={() => {
                  if (!row.id.startsWith("mrt-row-create")) {
                    const newValue = editedRows[row.id]?.[cell.column.id];
                    table.options.onEditingCellSave?.({
                      cell,
                      value: newValue,
                      row,
                      table,
                    });
                    table.setEditingCell(null);
                  }
                }}
                type={col.columnType}
                dateFormat={
                  col.columnType === "date"
                    ? setting.regionalSettings.dateFormat
                    : null
                }
                clientId={row.original?.client?.id}
                portfolioId={row.original?.portfolio?.id}
                parentType={parentType}
                table={table}
              />
            );
          },
          Header: ({ column }) => {
            return editingHeader === column.id ? (
              <input
                value={headerValue}
                onChange={(e) => setHeaderValue(e.target.value)}
                onBlur={() => handleHeaderSave(column.id)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    handleHeaderSave(column.id);
                  }
                }}
                autoFocus
                className="header-edit-input"
                style={{
                  minWidth: column.getSize() - 60,
                }}
              />
            ) : (
              <span>{column.columnDef.header}</span>
            );
          },
        };
      });
    setCols((prevCols) => {
      if (JSON.stringify(prevCols) !== JSON.stringify(columns)) {
        return columns;
      }
      return prevCols;
    });
  }, [colsList, rowData, editingHeader, headerValue, isFetchingMore]);

  const table = useMaterialReactTable({
    columns: cols,
    data: rows,
    enableSorting: true,
    manualSorting: false,
    getRowId: (row) => row.id,
    enableHiding: true,
    visibleInShowHideMenu: false,
    enablePagination: false,
    enableTopToolbar: true,
    enableGlobalFilter: true,
    enableStickyHeader: true,
    enableColumnFilterModes: true,
    enableColumnDragging: true,
    enableColumnOrdering: true,
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    editDisplayMode: "cell",
    globalFilterFn: "contains",
    enableEditing: false,
    enableFilters: false,
    enableCreatingRow: true,
    createDisplayMode: "row",
    positionCreatingRow: "bottom",
    initialState: {
      density: "compact",
      showGlobalFilter: false,
      showColumnFilters: false,
      enableHiding: false,
      columnOrder: cols.map((col) => col.order),
    },
    state: {
      sorting,
      isLoading: rows.length === 0 && projectLoading,
      columnSizing,
      columnOrder,
      showLoadingOverlay: false,
      showProgressBars: false,
    },
    onSortingChange: setSorting,
    onCreatingRowCancel: () => {
      setValidationErrors({});
      handleCancelAddRow();
    },
    onCreatingRowSave: ({ values, table }) => {
      const errors = {};
      if (!values.name) errors["name"] = `The Name field is required.`;
      if (!clientId && !values.client)
        errors["client"] = `The Client field is required.`;
      if (
        values.startDate &&
        values.endDate &&
        isDateGreaterThan(values.startDate, values.endDate)
      ) {
        errors[
          "endDate"
        ] = `End date must be equal to or greater than start date.`;
      }
      if (
        values.startDate &&
        values.revisedEndDate &&
        isDateGreaterThan(values.startDate, values.revisedEndDate)
      ) {
        errors[
          "revisedEndDate"
        ] = `Revised end date must be equal to or greater than start date.`;
      }

      if (Object.keys(errors).length) {
        dispatch(
          generalActions.showToast({
            title:
              errors.name ||
              errors.client ||
              errors.endDate ||
              errors.revisedEndDate,
            type: "error",
          })
        );
        return;
      }

      const payload = {
        name: values.name,
        ClientId: values?.client?.id || clientId,
        PortfolioId: values?.portfolio?.id || portfolioId,
        ProgramId: values?.program?.id || programId,
        ...(values?.owner && { OwnerId: values.owner.id }),
        status: values?.status?.id ?? 0,
        budget: values?.budget ?? 0,
      };
      handleCreate(payload);
      table.setCreatingRow(null);
    },
    onEditingRowCancel: () => {
      setValidationErrors({});
      setEditedRows({});
    },
    positionGlobalFilter: "left",
    onEditingCellSave: ({ cell, row }) => {
      const editedRow = editedRows[row.id];
      const newValue =
        editedRow?.[cell.column.id] !== undefined
          ? editedRow[cell.column.id]
          : cell.getValue();

      if (newValue === cell.getValue()) return; // Skip if no real change
      setRows((prevRows) =>
        prevRows.map((r, index) =>
          index === row.index ? { ...r, [cell.column.id]: newValue } : r
        )
      );

      // Remove the specific cell edit
      setEditedRows((prev) => {
        const updated = { ...prev };
        if (updated[row.id]) {
          delete updated[row.id][cell.column.id];
          if (Object.keys(updated[row.id]).length === 0) {
            delete updated[row.id];
          }
        }
        return updated;
      });

      const prePayload = {
        ...row.original,
        OwnerId:
          cell.column.id === "owner" ? newValue?.id : row.original.owner?.id,
        PortfolioId:
          cell.column.id === "portfolio"
            ? newValue?.id
            : row.original.portfolio?.id,
        ProgramId:
          cell.column.id === "program"
            ? newValue?.id
            : row.original.program?.id,
        [cell.column.id]:
          cell.column.id === "owner" ||
            cell.column.id === "portfolio" ||
            cell.column.id === "program"
            ? newValue
            : newValue,
      };
      const payload = formatPayload(prePayload);
      handleSave(row.original.id, {
        ...payload,
        ClientId: payload.client.id,
        status: prePayload?.status?.id ?? null,
      });
    },
    onColumnSizingChange: (updater) => {
      setColumnSizing((prev) => {
        const newSizing =
          typeof updater === "function" ? updater(prev) : updater;
        Object.entries(newSizing).forEach(([columnId, width]) => {
          setColumnWidth(tableName, columnId, width);
        });
        return newSizing;
      });
    },
    onColumnOrderChange: (updater) => {
      const newOrder = typeof updater === "function" ? updater() : updater;
      const neworderList = newOrder
        .filter((item) => item !== "mrt-row-spacer")
        .filter(Boolean);
      const areEqual = compareArrays(neworderList, columnOrder);
      if (areEqual) {
        return newOrder;
      }

      const sortedColumns = sortColumnsByOrderlist(colsList, neworderList);
      handleColumnOrdering(sortedColumns);
      setColumnOrder(neworderList);
      return neworderList;
    },
    muiTableHeadCellProps: ({ column }) => ({
      sx: {
        fontFamily: "Figtree-Medium",
        fontSize: "14px",
        padding: "1rem 0.5rem 1rem 1rem",
        backgroundColor: "#f8f7fc",
        color:"#342c82",
        height: "40px",
        fontWeight: 600,
        "&:hover": {
          backgroundColor: "#ece8fd",
          "& .MuiDivider-root": {
            opacity: 1,
          },
          "& .MuiButtonBase-root": {
            opacity: 1,
          },
          fontWeight: 700,
        },
        position: "relative",
        "& .Mui-TableHeadCell-Content": {
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          height: "100%",
        },
        "& .MuiTableSortLabel-root": {
          marginLeft: "8px",
          paddingTop: "3px",
          opacity: 0,
          "&:hover": {
            opacity: 1,
          },
        },
        "& .MuiDivider-root": {
          cursor: "col-resize",
          backgroundColor: "#342C82",
          opacity: 0,
        },
        "& .MuiButtonBase-root": {
          cursor: "grab",
          opacity: 0,
          "& .MuiSvgIcon-root": {
            path:{
              fill: "#342C82"
            }
          }
        },
      },
      onDoubleClick: (e) => {
        e.stopPropagation();
        e.preventDefault();
        handleHeaderDoubleClick(column.id, column.columnDef.header);
      },
    }),
    muiTopToolbarProps: {
      showColumnFilters: false,
      showGlobalFilter: true,
      enableHiding: false,
      sx: {
        position: "sticky",
        top: 0,
        zIndex: 1,
        "& .MuiBox-root": {
          padding: 0,
          display: "flex",
          alignItems: "center",
        },
      },
    },
    renderTopToolbar: () => (
      <ListingHeader
        isHeader={isHeader}
        parentType={parentType ?? "Project"}
        type="Project"
        btnLable="Add Project"
        searchedData={searchHandler}
        setIsCreate={() => setIsCreate(true)}
        rightPane={true}
        clientId={clientId}
        portfolioId={portfolioId}
        programId={programId}
        projectId={projectId}
        isCreatable={true}
        isSearchDisable={cols.length}
        handleCreateFunc={() => {
          handleAddRow();
          table.setCreatingRow(true);
        }}
        triggerSearch={triggerSearch}
      />
    ),
    renderBottomToolbarCustomActions: () => (
      <div className="bottom-toolbar-container">
        <div className="toolbar-content">
          {projectLoading || isFetchingMore ? (
            <div className="loading-indicator">
              <span>{`${!isFetchingMore ? 'Loading...' : 'Loading more data...'}`}</span>
            </div>
          ) : pagination?.TotalCount > 0 ? (
            <div className="data-info">
              <span className="data-text">
                Showing <strong>{rowData.length}</strong> of{" "}
                <strong>{pagination?.TotalCount || 0}</strong> total records
              </span>
            </div>
          ) : (
            <div className="data-info">
              <span className="data-text">No records Found</span>
            </div>
          )}
        </div>
      </div>
    ),
    muiBottomToolbarProps: {
      sx: {
        position: "absolute",
        bottom: 0,
        backgroundColor: "#f8f7fc",
        padding: "0.7rem 1rem",
        width: "100%",
        height: "38px"
      },
    },
    muiTableBodyProps: {
      sx: {
        fontFamily: "Figtree-Regular",
        fontSize: "13px",

        marginBottom: "66px",
        "& .MuiTableBody-Cell": {
          "&:hover": {
            backgroundColor: "#ece8fd15",
          },
        },
      },
    },
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        height: "32px",
        position: "relative",
        "&:hover": {
          backgroundColor: row.original.isSkeleton ? "transparent" : "#ece8fd15",
        },
        cursor: row.original.isSkeleton ? "default" : "pointer",
      },
      className: row.original.isSkeleton ? "" : "hoverable-row",
    }),
    muiTableContainerProps: ({ table }) => ({
      ref: tableContainerRef,
      sx: {
        height: `calc(100% - ${table.refs.topToolbarRef.current?.offsetHeight || parentType
          ? 115
          : 70
          }px)`,
        overflow: "auto",
        "&::-webkit-scrollbar": {
          height: "8px",
          width: "4px",
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: "#FFFFFF",
          borderRadius: "8px",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "#342c82",
          borderRadius: "8px",
          "&:hover": {
            backgroundColor: "#342c82",
          },
        }
      },
    }),
    muiTablePaperProps: {
      sx: {
        height: "100%",
      },
    },
    muiSkeletonProps: {
      height: 34,
      sx: {
        background:
          "linear-gradient(90deg, #e0e0e0 25%, #ECE7FD 50%, #e0e0e0 75%)",
        backgroundSize: "200% 100%",
        animation: "shimmer 1.5s infinite",
        "@keyframes shimmer": {
          "0%": {
            backgroundPosition: "200% 0",
          },
          "100%": {
            backgroundPosition: "-200% 0",
          },
        },
      },
    },
    muiLinearProgressProps: {
      sx: {
        height: "4px",
        backgroundColor: "#cfc9ff",
        "& .MuiLinearProgress-bar": {
          backgroundColor: "#342c82",
        },
        "& .MuiLinearProgress-bar1Indeterminate": {
          backgroundColor: "#342c82",
        },
        "& .MuiLinearProgress-bar2Indeterminate": {
          backgroundColor: "#342c82",
        },
        "& .MuiLinearProgress-barColorPrimary": {
          backgroundColor: "#342c82",
        },
        "& .MuiLinearProgress-colorPrimary": {
          backgroundColor: "#cfc9ff",
        },
        // Override any secondary color variants
        "& .MuiLinearProgress-colorSecondary": {
          backgroundColor: "#cfc9ff",
        },
        "& .MuiLinearProgress-barColorSecondary": {
          backgroundColor: "#342c82",
        },
      },
    },
    muiTableBodyCellProps: ({ cell, column, table }) => ({
      sx: {
        fontFamily: "Figtree-Regular",
        fontSize: "13px",
        padding: "0.5rem 1rem",
        position: "relative",
        overflow: "visible",
        "&:focus": {
          outline: "none",
        },
        "&:hover": {
          fontFamily: "Figtree-Medium",
          fontWeight: "bold",
        },
      },
    }),
    renderEmptyRowsFallback: () => {
      return projectLoading ? (
        <div className="d-flex justify-content-center">
          <Spin />
        </div>
      ) : rows.length === 0 ? (
        <div className="flex-grow flex items-center justify-center p-4">
          <div className="h-full flex flex-col items-center justify-center min-h-[400px] p-8">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="65"
              height="64"
              viewBox="0 0 65 64"
              fill="none"
              className="mb-4"
            >
              <path
                d="M6.9002 21.6008C6.9002 16.7407 10.8401 12.8008 15.7002 12.8008H49.3002C54.1603 12.8008 58.1002 16.7407 58.1002 21.6008V42.4008C58.1002 47.2609 54.1603 51.2008 49.3002 51.2008H32.8884L27.8968 46.2092C28.7964 44.3293 29.3002 42.2239 29.3002 40.0008C29.3002 32.0479 22.8531 25.6008 14.9002 25.6008C11.94 25.6008 9.18835 26.494 6.9002 28.0257V21.6008ZM14.9002 51.2008C17.416 51.2008 19.7381 50.3713 21.6079 48.9708L29.7688 57.132C30.3936 57.7569 31.4067 57.7569 32.0315 57.1321C32.6564 56.5072 32.6564 55.4942 32.0316 54.8693L23.8706 46.708C25.2708 44.8384 26.1002 42.5164 26.1002 40.0008C26.1002 33.8152 21.0858 28.8008 14.9002 28.8008C8.71461 28.8008 3.7002 33.8152 3.7002 40.0008C3.7002 46.1864 8.71461 51.2008 14.9002 51.2008ZM14.9002 48.0008C10.4819 48.0008 6.9002 44.4191 6.9002 40.0008C6.9002 35.5825 10.4819 32.0008 14.9002 32.0008C19.3185 32.0008 22.9002 35.5825 22.9002 40.0008C22.9002 44.4191 19.3185 48.0008 14.9002 48.0008Z"
                fill="#342C82"
              />
            </svg>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">
              No data currently available!
            </h3>
            <p className="text-gray-600 text-center max-w-md">
              New entries will appear here once created.
            </p>
          </div>
        </div>
      ) : null;
    },
  });

  return <MaterialReactTable table={table} />;
};

export default ProjectTable;
